import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import { timeDifference } from "../utils/timeUtils";
import { handleImageError, pickClusterImage } from "../utils/imageUtils";
import { SectionHeader } from "./redesign/Sections";

const ClusterPage = () => {
  const { category, clusterId } = useParams();
  const navigate = useNavigate();
  const [clusterData, setClusterData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setError(null);
    axios
      .get(
        `${process.env.REACT_APP_API_URL}/category/${category}/cluster/${clusterId}`
      )
      .then((response) => setClusterData(response.data))
      .catch((err) => {
        console.error("Error fetching cluster data:", err);
        setError(err);
        if (err.response && err.response.status === 404) {
          setTimeout(() => navigate(`/category/${category}`), 3000);
        }
      });
  }, [category, clusterId, navigate]);

  if (error) {
    return (
      <div className="rounded-card border border-line bg-surface p-6">
        <h2 className="font-head text-[20px] font-bold text-ink">
          Gabim gjatë ngarkimit
        </h2>
        <p className="mt-2 text-[14px] text-sub">
          Po ridrejtoheni në kategorinë "{category}"…
        </p>
        <Link
          to={`/category/${category}`}
          className="mt-3 inline-block font-head font-semibold text-brand hover:underline"
        >
          ← Kthehu te {category}
        </Link>
      </div>
    );
  }

  if (!clusterData) return null;

  const articles = clusterData.articles || [];
  const headline = articles[0]?.title || "";
  const sources = [...new Set(articles.map((a) => a.portal).filter(Boolean))];
  const heroImage = pickClusterImage(articles) || "/fallback.jpg";

  return (
    <div>
      <Link
        to={`/category/${category}`}
        className="mb-3 inline-block font-head text-[12.5px] font-semibold text-sub hover:text-brand"
      >
        ← {category}
      </Link>

      <article className="overflow-hidden rounded-card border border-line bg-surface">
        <div className="relative h-[240px] bg-gradient-to-br from-[#7fb3c9] via-[#3d6f8a] to-[#22384a]">
          <img
            src={heroImage}
            alt=""
            referrerPolicy="no-referrer"
            className="h-full w-full object-cover"
            onError={(e) => handleImageError(e, heroImage)}
          />
          <span className="absolute left-4 top-4 rounded-md bg-brand px-2.5 py-1.5 font-head text-[11px] font-bold uppercase tracking-wider text-white">
            {category}
          </span>
        </div>
        <div className="p-6">
          <h1 className="font-head text-[26px] font-bold leading-tight tracking-tight text-ink lg:text-[30px]">
            {headline}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2.5">
            <span className="rounded-pill bg-ai-bg px-3 py-1.5 font-head text-[12px] font-bold text-ai">
              ◆ {articles.length} lajme · {sources.length} burime
            </span>
            {articles[0]?.fetch_date && (
              <span className="text-[12.5px] text-sub">
                {timeDifference(articles[0].fetch_date)}
              </span>
            )}
          </div>
        </div>
      </article>

      <div className="mt-8">
        <SectionHeader title="Të gjitha raportet" />
        <ul className="flex flex-col">
          {articles.map((article) => (
            <li
              key={article.link}
              className="grid grid-cols-[100px_1fr] gap-4 border-t border-line py-4 first:border-t-0 md:grid-cols-[140px_1fr] md:gap-5"
            >
              <div className="h-[72px] overflow-hidden rounded-card bg-chip md:h-[92px]">
                <img
                  src={article.image_url || "/fallback.jpg"}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="h-full w-full object-cover"
                  onError={(e) => handleImageError(e, article.image_url)}
                />
              </div>
              <div className="min-w-0">
                <a
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:underline"
                >
                  <h3 className="font-head text-[15.5px] font-bold leading-snug tracking-tight text-ink md:text-[17px]">
                    {article.title}
                  </h3>
                </a>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <span className="font-head text-[11.5px] font-semibold text-brand">
                    {article.portal}
                  </span>
                  <span className="text-[12px] text-sub">
                    · {timeDifference(article.fetch_date)}
                  </span>
                </div>
                {article.summary && (
                  <p className="mt-2 text-[13px] leading-relaxed text-sub">
                    {article.summary.split(" ").slice(0, 50).join(" ") + "…"}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ClusterPage;
